import { Button } from "@/components/ui/button.tsx";
import { useCreateProject, useProjects } from "@/hooks/useProjects";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { HTMLMotionProps, motion } from "framer-motion";
import { FileText, Plus } from "lucide-react";
import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GuidedProjectCreation } from "./guided-project-creation";

interface ProjectListProps extends HTMLMotionProps<"div"> {
    searchQuery?: string;
}

// Add the NewProjectButton to the ProjectListComponent type
interface ProjectListComponentType extends React.FC<ProjectListProps> {
    NewProjectButton: React.FC;
}

const containerVariants = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            when: "beforeChildren"
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3 }
    }
};

export const ProjectListComponent: ProjectListComponentType = ({ className, searchQuery = "", ...props }: ProjectListProps) => {
    const navigate = useNavigate();

    // Use the React Query hook instead of manual fetching
    const {
        data: projects = [],
        isLoading,
        isError
    } = useProjects();

    const filteredProjects = projects.filter(project =>
        project.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // State for guided project creation
    const [showGuidedCreation, setShowGuidedCreation] = useState(false);
    const createProjectMutation = useCreateProject();

    const onOpenProject = (projectId: string) => {
        console.log("open project", projectId);
        
        navigate(`/projects/${projectId}`);
    };

    const handleProjectCreationComplete = (projectId: string) => {
        onOpenProject(projectId);
    };

    const createProject = () => {
        setShowGuidedCreation(true);
    };

    // Helper to get random status color for demo (in real app, this would come from project data)
    const getStatusColor = (id: string | number | undefined) => {
        const statuses = [
            { label: 'Drafting', color: 'bg-emerald-500' },
            { label: 'Polishing', color: 'bg-blue-500' },
            { label: 'Outline', color: 'bg-amber-500' },
            { label: 'Idea', color: 'bg-gray-500' }
        ];

        if (!id) return statuses[0];

        // Ensure id is a string
        const idStr = String(id);

        // Deterministic pseudo-random based on ID char code sum
        const index = idStr.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % statuses.length;
        return statuses[index];
    };

    // Prepare UI components based on state
    const loadingContent = (
        <motion.div className={cn("space-y-6", className)} {...props}>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-2xl border border-border/5 bg-card/40 p-6 h-[200px] animate-pulse">
                        <div className="h-6 w-1/2 bg-muted/20 rounded mb-4"></div>
                        <div className="h-4 w-full bg-muted/10 rounded mb-2"></div>
                        <div className="h-4 w-2/3 bg-muted/10 rounded"></div>
                    </div>
                ))}
            </div>
        </motion.div>
    );

    const errorContent = (
        <motion.div className={cn("space-y-6", className)} {...props}>
            <GuidedProjectCreation
                open={showGuidedCreation}
                onClose={() => setShowGuidedCreation(false)}
                onComplete={handleProjectCreationComplete}
            />
            <div className="flex flex-col items-center justify-center text-center border border-dashed border-border rounded-2xl p-12 min-h-[300px] bg-red-500/5">
                <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4 text-red-500">
                    <FileText className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-medium mb-2 text-red-500">Failed to load projects</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                    We couldn't fetch your projects. Please try again later.
                </p>
                <Button onClick={() => window.location.reload()} variant="outline" className="border-red-500/20 hover:bg-red-500/10 text-red-500">
                    Retry
                </Button>
            </div>
        </motion.div>
    );

    const emptyContent = (
        <motion.div className={cn("space-y-6", className)} {...props}>
            <GuidedProjectCreation
                open={showGuidedCreation}
                onClose={() => setShowGuidedCreation(false)}
                onComplete={handleProjectCreationComplete}
            />
            <div className="flex flex-col items-center justify-center text-center border border-dashed border-border rounded-2xl p-12 min-h-[300px]">
                <div className="h-12 w-12 rounded-full bg-muted/10 flex items-center justify-center mb-4 text-muted-foreground">
                    <Plus className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-medium mb-2">No projects found</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                    {searchQuery ? `No projects match "${searchQuery}"` : "Create your first project to get started."}
                </p>
                {!searchQuery && (
                    <Button onClick={createProject} className="bg-foreground text-background hover:bg-foreground/90 rounded-full px-6">
                        Create Project
                    </Button>
                )}
            </div>
        </motion.div>
    );

    if (isLoading || createProjectMutation.isPending) return loadingContent;
    if (isError) return errorContent;
    if (filteredProjects.length === 0) return emptyContent;

    return (
        <motion.div
            {...props}
            className={cn("space-y-6", className)}
        >
            <GuidedProjectCreation
                open={showGuidedCreation}
                onClose={() => setShowGuidedCreation(false)}
                onComplete={handleProjectCreationComplete}
            />

            <motion.div
                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                variants={containerVariants}
                initial="hidden"
                animate="show"
            >
                {filteredProjects.map((project) => {
                    return (
                        <motion.div
                            key={project.id}
                            variants={itemVariants}
                            whileHover={{ y: -4, transition: { duration: 0.1, ease: "easeOut" } }}
                            onClick={() => onOpenProject(project.id)}
                            className="group relative flex flex-col justify-between rounded-2xl border border-border bg-card p-6 h-[220px] cursor-pointer hover:border-primary/50 hover:shadow-xl hover:shadow-black/5 transition-all"
                        >
                            <div>
                                <h3 className="font-semibold text-xl text-card-foreground mb-3 line-clamp-1 tracking-tight">
                                    {project.name}
                                </h3>
                                <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                                    {project.description || <span className="italic opacity-50">No description provided.</span>}
                                </p>
                            </div>

                            <div className="pt-4 border-t border-border flex items-center justify-between text-xs font-medium">
                                <span className="text-muted-foreground">
                                    {format(new Date(project.created_at), 'MMM d, yyyy')}
                                </span>
                            </div>
                        </motion.div>
                    );
                })}
            </motion.div>
        </motion.div>
    );
};

// Define the NewProjectButton component outside the main component
const NewProjectButton: React.FC = () => {
    const [showGuidedCreation, setShowGuidedCreation] = useState(false);
    const navigate = useNavigate();

    const handleProjectCreationComplete = (projectId: string) => {
        navigate(`/projects/${projectId}`);
    };

    return (
        <>
            <GuidedProjectCreation
                open={showGuidedCreation}
                onClose={() => setShowGuidedCreation(false)}
                onComplete={handleProjectCreationComplete}
            />
            <Button
                onClick={() => setShowGuidedCreation(true)}
                size="default"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-semibold px-6 shadow-lg shadow-black/10"
            >
                New Project
            </Button>
        </>
    );
};

// Attach the NewProjectButton to the ProjectListComponent
ProjectListComponent.NewProjectButton = NewProjectButton;